import React from "react";
import useStoreAnimation from "../store/useStoreAnimation";

interface AnimationProps {
    id: string;
    ref: React.RefObject<HTMLDivElement>;
}

const useAnimation = ({ id, ref }: AnimationProps) => {
    const queue = useStoreAnimation(state => state.animations[id]);
    const dequeue = useStoreAnimation(state => state.dequeue);

    React.useLayoutEffect(() => {
        if(!ref.current || document.hidden) {
            return;
        }

        const animation = dequeue(id);

        if(!animation) {
            return;
        }

        const { initial, effects: rawEffects, transitions, onAnimationEnd } = animation;
        const effects = typeof rawEffects === "function" ? rawEffects(ref.current) : (rawEffects || []);

        if(initial) {
            for(const [property, value] of Object.entries(initial)) {
                (ref.current.style as unknown as Record<string, string>)[property] = String(value);
            }
        }

        let animationsCompleted = 0;
        const totalAnimations = effects.length;

        if(totalAnimations === 0) {
            if(onAnimationEnd) {
                setTimeout(onAnimationEnd, animation.duration);
            }

            return;
        }

        effects.forEach((effect, i) => {
            const element = effect.el ? ref.current?.querySelector(effect.el) : ref.current;

            if(!element) {
                return;
            }

            const animate = element.animate(effect, transitions && transitions[i] ? transitions[i] : { duration: 250 });

            const onComplete = () => {
                animationsCompleted++;

                if(animationsCompleted === totalAnimations) {
                    onCustomAnimationEnd();
                }
            };

            const onCustomAnimationEnd = () => {
                onAnimationEnd?.();

                if(initial && element) {
                    for(const [property] of Object.entries(initial)) {
                        ((element as HTMLElement).style as unknown as Record<string, string>)[property] = "";
                    }
                }
            };

            animate.onfinish = onComplete;
            animate.oncancel = onCustomAnimationEnd;
        });
    }, [queue]);
};

export default useAnimation;
